/**
 * Map Viewport Plugin for Search Results Map Variant
 *
 * Translates external signals into map viewport changes — answering the question
 * "given this state change, where should the map be looking?"
 *
 * Current responsibilities:
 * - Re-center the map when the user picks a new search origin from the typeahead
 *
 * Future candidates for this plugin (do NOT add to other plugins):
 * - Re-centering on geolocation (currently in searchWithinMap.plugin)
 * - Zoom resets driven by location type changes
 * - Any other "external event → pan/zoom" mapping
 *
 * This plugin does NOT:
 * - Create or tear down the map instance (see map.plugin)
 * - Trigger searches or manage the search-within-map button (see searchWithinMap.plugin)
 * - React to KML boundary data (see mapBoundaries.plugin)
 *
 * Reason to change: how or when the map viewport should pan/zoom in response
 * to a change in search context.
 *
 * Recenter-on-selection reacts to locationSelected$ (redux/locationSelectedEpic),
 * not to a derived Redux state comparison — re-selecting the location that is
 * already active must still recenter the map (the user has often panned away from
 * it), and that can't be detected by diffing state values, since the resulting
 * value is identical to before.
 *
 * @module searchResultsMap/plugins/mapViewport.plugin
 */

import { LocationType, selectLocation, selectLocationType } from 'features/searchResultsFacets';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  skip,
  startWith,
  withLatestFrom
} from 'rxjs';
import { DEFAULT_ZOOM_LEVELS } from '../constants';
import { locationSelected$ } from '../redux/observables';
import locationSelectedEpic from '../redux/locationSelectedEpic';

const RECTANGULAR_AREA = LocationType.RectangularArea.toString();

export default function createMapViewportPlugin(mapToggle$) {
  const state = {
    started: false,
    mapFacade: null,
    store$: null,
    getBreakpoint: null,
    subscriptions: [],
  };

  function mapToggleByBreakpoint(breakpoint) {
    return breakpoint === LayoutBreakpoint.Big || breakpoint === LayoutBreakpoint.Medium;
  }

  function subscribeToOriginCoordinates() {
    // Shared stream of valid, distinct origin coordinates.
    // shareReplay lets the toggle subscription sample the latest value without re-deriving it.
    const origin$ = state.store$.pipe(
      map(selectLocation),
      map((location) => ({
        originLatitude: Number(location?.originLatitude),
        originLongitude: Number(location?.originLongitude),
      })),
      filter(
        ({ originLatitude, originLongitude }) =>
          !Number.isNaN(originLatitude) && !Number.isNaN(originLongitude)
      ),
      distinctUntilChanged(
        (prev, curr) =>
          prev.originLatitude === curr.originLatitude &&
          prev.originLongitude === curr.originLongitude
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    // Re-center whenever the user explicitly selects a location, but only if the
    // map is currently visible. Driven by locationSelected$ (an action-level signal)
    // instead of origin$ above — re-selecting the location that is already active
    // must still recenter, and origin$'s value-based distinctUntilChanged would
    // otherwise treat that as "unchanged" and silently skip it.
    const onLocationSelected = locationSelected$
      .pipe(
        withLatestFrom(mapToggle$),
        filter(([, isMapToggled]) => mapToggleByBreakpoint(state.getBreakpoint()) || isMapToggled),
        map(([coords]) => coords),
      )
      .subscribe(({ latitude, longitude }) => {
        state.mapFacade?.updateMapCenter?.({ latitude, longitude });
      });

    // On mobile: when the user opens the map, re-center to the latest origin so
    // they always see the location they searched for.
    const onMapVisible = mapToggle$
      .pipe(
        filter(Boolean),
        filter(() => !mapToggleByBreakpoint(state.getBreakpoint())),
        withLatestFrom(origin$),
        map(([, coords]) => coords),
      )
      .subscribe(({ originLatitude, originLongitude }) => {
        state.mapFacade?.updateMapCenter?.({ latitude: originLatitude, longitude: originLongitude });
      });

    state.subscriptions.push(onLocationSelected, onMapVisible);
  }

  // When the user navigates from one location type to another (e.g. state → county)
  // the map instance is reused but its zoom level is not automatically reset.
  // This subscription detects location type changes after the initial load and
  // applies the correct default zoom so the previous type's zoom is not reused.
  //
  // Exception: when entering rectangular-area mode via Remove Boundary,
  // preserve the user's current zoom instead of snapping to defaults.
  function subscribeToLocationTypeZoomReset() {
    const zoomReset = state.store$.pipe(
      map(selectLocationType),
      distinctUntilChanged(),
      startWith(null),
      pairwise(),
      skip(1), // initial zoom is set by map.plugin when the map is first created
      filter(([, current]) => current !== RECTANGULAR_AREA),
      map(([, current]) => current),
    ).subscribe((currentLocationType) => {
      const zoom = DEFAULT_ZOOM_LEVELS[currentLocationType] ?? DEFAULT_ZOOM_LEVELS.default;
      state.mapFacade?.setMapZoom?.(zoom);
    });

    state.subscriptions.push(zoomReset);
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'map-viewport',

    async init(context) {
      context.services.epic$.next(locationSelectedEpic);

      context.state.mapViewport = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.getBreakpoint = context.services.getBreakpoint;

      if (!state.mapFacade || !state.store$) {
        return;
      }

      subscribeToOriginCoordinates();
      subscribeToLocationTypeZoomReset();

      state.started = true;
      context.state.mapViewport = {
        started: true,
      };
    },

    async stop(context) {
      clearSubscriptions();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.getBreakpoint = null;

      delete context.state.mapViewport;
    },
  };
}
